import { useEffect, useMemo, useRef, useState } from 'react';
import { getRoleLabel } from '../utils/permissions';
import './TeamSelector.css';

function TeamSelector({
  teams,
  selectedTeamIds,
  onSelectedTeamIdsChange,
  useDynamicTeam,
  onUseDynamicTeamChange,
  dynamicTeamName,
  onDynamicTeamNameChange,
  dynamicMemberIds,
  onDynamicMemberIdsChange,
  assignableUsers,
  conflictTeamIds = new Set(),
  conflictMemberIds = new Set(),
}) {
  const [query, setQuery] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [memberOpen, setMemberOpen] = useState(false);
  const rootRef = useRef(null);
  const memberRef = useRef(null);
  const teamListboxId = 'team-selector-listbox';
  const memberListboxId = 'team-selector-member-listbox';

  const selectedTeams = useMemo(
    () =>
      teams.filter((team) => selectedTeamIds.includes(String(team.id))),
    [teams, selectedTeamIds],
  );

  const filteredTeams = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return teams.filter((team) => {
      if (selectedTeamIds.includes(String(team.id))) {
        return false;
      }
      if (!normalized) {
        return true;
      }
      return team.name.toLowerCase().includes(normalized);
    });
  }, [teams, selectedTeamIds, query]);

  const selectedMembers = useMemo(
    () =>
      assignableUsers.filter((user) =>
        dynamicMemberIds.includes(String(user.id)),
      ),
    [assignableUsers, dynamicMemberIds],
  );

  const filteredMembers = useMemo(() => {
    const normalized = memberQuery.trim().toLowerCase();
    return assignableUsers.filter((user) => {
      if (dynamicMemberIds.includes(String(user.id))) {
        return false;
      }
      if (!normalized) {
        return true;
      }
      const label = `${user.name || ''} ${user.email || ''} ${getRoleLabel(user)}`;
      return label.toLowerCase().includes(normalized);
    });
  }, [assignableUsers, dynamicMemberIds, memberQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
      if (memberRef.current && !memberRef.current.contains(event.target)) {
        setMemberOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTeam = (teamId) => {
    const id = String(teamId);
    if (selectedTeamIds.includes(id)) {
      return;
    }
    onSelectedTeamIdsChange([...selectedTeamIds, id]);
    setQuery('');
    setOpen(false);
  };

  const removeTeam = (teamId) => {
    onSelectedTeamIdsChange(
      selectedTeamIds.filter((id) => id !== String(teamId)),
    );
  };

  const enableDynamicTeam = () => {
    onUseDynamicTeamChange(true);
    setQuery('');
    setOpen(false);
  };

  const disableDynamicTeam = () => {
    onUseDynamicTeamChange(false);
    onDynamicTeamNameChange('');
    onDynamicMemberIdsChange([]);
    setMemberQuery('');
  };

  const addMember = (userId) => {
    const id = String(userId);
    if (dynamicMemberIds.includes(id)) {
      return;
    }
    onDynamicMemberIdsChange([...dynamicMemberIds, id]);
    setMemberQuery('');
    setMemberOpen(false);
  };

  const removeMember = (userId) => {
    onDynamicMemberIdsChange(
      dynamicMemberIds.filter((id) => id !== String(userId)),
    );
  };

  return (
    <div className="team-selector">
      <div className="team-selector__selected">
        {selectedTeams.length === 0 && !useDynamicTeam && (
          <span className="team-selector__placeholder">
            No teams selected yet
          </span>
        )}

        {selectedTeams.map((team) => (
          <span
            key={team.id}
            className={`team-selector__chip ${
              conflictTeamIds.has(String(team.id))
                ? 'team-selector__chip--conflict'
                : ''
            }`}
          >
            <span>
              {team.name}
              {conflictTeamIds.has(String(team.id)) ? ' · clash' : ''}
            </span>
            <button
              type="button"
              aria-label={`Remove ${team.name}`}
              onClick={() => removeTeam(team.id)}
            >
              ×
            </button>
          </span>
        ))}

        {useDynamicTeam && (
          <span
            className={`team-selector__chip team-selector__chip--dynamic ${
              dynamicMemberIds.some((id) => conflictMemberIds.has(String(id)))
                ? 'team-selector__chip--conflict'
                : ''
            }`}
          >
            <span>
              Dynamic team
              {dynamicTeamName ? `: ${dynamicTeamName}` : ''}
              {dynamicMemberIds.some((id) => conflictMemberIds.has(String(id)))
                ? ' · clash'
                : ''}
            </span>
            <button
              type="button"
              aria-label="Remove dynamic team"
              onClick={disableDynamicTeam}
            >
              ×
            </button>
          </span>
        )}
      </div>

      <div className="team-selector__combobox" ref={rootRef}>
        <input
          type="text"
          role="combobox"
          className="team-selector__input"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search and select teams..."
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={teamListboxId}
        />

        {open && (
          <ul className="team-selector__dropdown" role="listbox" id={teamListboxId}>
            <li role="option" aria-selected={useDynamicTeam}>
              <button
                type="button"
                className="team-selector__option team-selector__option--dynamic"
                onClick={enableDynamicTeam}
                disabled={useDynamicTeam}
              >
                + Create dynamic team for this schedule
              </button>
            </li>

            {filteredTeams.length === 0 ? (
              <li className="team-selector__empty">
                {teams.length === 0
                  ? 'No static teams yet. Use dynamic team option above.'
                  : 'No matching teams'}
              </li>
            ) : (
              filteredTeams.map((team) => (
                <li
                  key={team.id}
                  role="option"
                  aria-selected={selectedTeamIds.includes(String(team.id))}
                >
                  <button
                    type="button"
                    className="team-selector__option"
                    onClick={() => addTeam(team.id)}
                  >
                    <span className="team-selector__option-name">{team.name}</span>
                    <span className="team-selector__option-meta">
                      {Array.isArray(team.members)
                        ? `${team.members.length} members`
                        : 'Team'}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {useDynamicTeam && (
        <div className="team-selector__dynamic-panel">
          <label>
            Dynamic team name
            <input
              type="text"
              value={dynamicTeamName}
              onChange={(event) => onDynamicTeamNameChange(event.target.value)}
              placeholder="Schedule squad – March"
              required
            />
          </label>

          <div className="team-selector__member-picker" ref={memberRef}>
            <label>Members</label>

            <div className="team-selector__selected team-selector__selected--members">
              {selectedMembers.length === 0 ? (
                <span className="team-selector__placeholder">
                  Add members to the dynamic team
                </span>
              ) : (
                selectedMembers.map((user) => (
                  <span key={user.id} className="team-selector__chip">
                    <span>{user.name || user.email}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${user.name || user.email}`}
                      onClick={() => removeMember(user.id)}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>

            <input
              type="text"
              role="combobox"
              className="team-selector__input"
              value={memberQuery}
              onChange={(event) => {
                setMemberQuery(event.target.value);
                setMemberOpen(true);
              }}
              onFocus={() => setMemberOpen(true)}
              placeholder="Search users to add..."
              aria-expanded={memberOpen}
              aria-haspopup="listbox"
              aria-controls={memberListboxId}
            />

            {memberOpen && (
              <ul
                className="team-selector__dropdown"
                role="listbox"
                id={memberListboxId}
              >
                {filteredMembers.length === 0 ? (
                  <li className="team-selector__empty">No matching users</li>
                ) : (
                  filteredMembers.map((user) => (
                    <li
                      key={user.id}
                      role="option"
                      aria-selected={dynamicMemberIds.includes(String(user.id))}
                    >
                      <button
                        type="button"
                        className="team-selector__option"
                        onClick={() => addMember(user.id)}
                      >
                        <span className="team-selector__option-name">
                          {user.name || user.email}
                        </span>
                        <span className="team-selector__option-meta">
                          {getRoleLabel(user)}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamSelector;
